import { formatDateOnly } from './date-only';
import { categoryPathLabel } from './period-tree';
import type {
  ClassificationProposal,
  ClassificationStatus,
  PeriodLabel,
  ReviewableClassificationStatus,
  RoomDocument,
  SetBy,
} from '../api/types';

/** A document the AI did not file at its final level (`low_confidence`) or could
 * not file at all (`failed`) — the ones a person is asked to file. */
export function isNeedsReview(
  status: ClassificationStatus | undefined,
): status is ReviewableClassificationStatus {
  return status === 'low_confidence' || status === 'failed';
}

/** The listing's period tag is named "Period: Annual"; the document carries "Annual". */
const PERIOD_PREFIX = 'Period: ';

/**
 * The period choices a person can set, with how each reads. The period is only
 * the cadence; the year/month is the date of data.
 */
export const PERIOD_OPTIONS: readonly { value: PeriodLabel; label: string }[] = [
  { value: 'Annual', label: 'Annual' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'YTD', label: 'Year to date (YTD)' },
  { value: 'TTM', label: 'Trailing 12 months (TTM)' },
];

/** Who set a value, in plain words: "Set by AI" / "Set by a person". */
export function setByLabel(setBy: SetBy | null | undefined): string | null {
  if (setBy === 'ai') return 'Set by AI';
  if (setBy === 'person') return 'Set by a person';
  return null;
}

/**
 * The fixed start of the reasoning the classifier stores with a date it READ
 * rather than judged (server: classification/classify.ts
 * `DATE_READ_REASONING_PREFIX`): "Date of data read from the file name".
 */
export const DATE_READ_REASONING_PREFIX = 'Date of data read ';

/**
 * Where a suggested date was read — "from the file name", "from the statement
 * date", "from the contract title", "from the signature date" — or null when
 * the AI judged it (owner 2026-10-15: a date the file name states is not "50%").
 */
export function dateSourceLabel(
  proposal: Pick<ClassificationProposal, 'tagType' | 'reasoning'>,
): string | null {
  if (proposal.tagType !== 'date') return null;
  const reasoning = proposal.reasoning ?? '';
  if (!reasoning.startsWith(DATE_READ_REASONING_PREFIX)) return null;
  const where = reasoning.slice(DATE_READ_REASONING_PREFIX.length).trim();
  return where || null;
}

/**
 * How sure a suggestion is, as people read it: where a date was read from, or
 * the AI's confidence as a whole percentage ("87%").
 */
export function suggestionCertainty(
  proposal: Pick<ClassificationProposal, 'tagType' | 'reasoning' | 'confidence'>,
): string {
  return dateSourceLabel(proposal) ?? `${Math.round(proposal.confidence * 100)}%`;
}

/** A pending AI suggestion for one of a document's date fields. */
export interface DateFieldSuggestion {
  proposal: ClassificationProposal;
  /** The suggested value as people read it: "Quarterly", "Dec 31, 2023". */
  value: string;
}

/**
 * The document's pending AI suggestions for its PERIOD and its DATE OF DATA,
 * highest confidence first. These are the only date values the AI left as a
 * suggestion (a confident one is applied straight away and is not pending).
 */
export function dateFieldSuggestions(
  documentId: string,
  proposals: readonly ClassificationProposal[],
): { period: DateFieldSuggestion[]; date: DateFieldSuggestion[] } {
  const mine = proposals
    .filter((p) => p.documentId === documentId)
    .sort((a, b) => b.confidence - a.confidence);
  const period: DateFieldSuggestion[] = [];
  const date: DateFieldSuggestion[] = [];
  for (const p of mine) {
    const name = p.tagName ?? '';
    if (p.tagType === 'date' && name) {
      date.push({ proposal: p, value: formatDateOnly(name, name) });
    } else if (p.tagType === 'custom' && name.startsWith(PERIOD_PREFIX)) {
      period.push({ proposal: p, value: name.slice(PERIOD_PREFIX.length) });
    }
  }
  return { period, date };
}

/**
 * One line saying where a document is filed and under what date — the
 * viewer's header line: "Tax › Federal › 2023 Return · Annual · Dec 31, 2023".
 * Nothing filed and no date reads "Not filed yet".
 */
export function filingSummary(
  document: Pick<RoomDocument, 'categoryPath' | 'period' | 'dataDate'>,
): string {
  const parts: string[] = [];
  if (document.categoryPath.length > 0) parts.push(categoryPathLabel(document.categoryPath, ' › '));
  else parts.push('Not filed yet');
  if (document.period) parts.push(document.period);
  if (document.dataDate) parts.push(formatDateOnly(document.dataDate));
  return parts.join(' · ');
}
